import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';

type CookieCategory = 'necessary' | 'analytics' | 'marketing' | 'functional';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

interface Consent {
  accepted: boolean;
  preferences: CookiePreferences;
  timestamp?: string;
}

interface CookieConsentContextType {
  consent: Consent | null;
  bannerOpen: boolean;
  modalOpen: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (p: CookiePreferences) => void;
  openModal: () => void;
  closeModal: () => void;
}

const STORAGE_KEY = 'zuri-cookie-consent';

const defaultPreferences: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
  functional: false,
};

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<Consent | null>(null);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        setConsent(JSON.parse(raw));
      } else {
        setBannerOpen(true);
      }
    } catch {
      setBannerOpen(true);
    }
  }, []);

  const persist = useCallback((next: Consent) => {
    setConsent(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }, []);

  const acceptAll = useCallback(() => {
    persist({ accepted: true, preferences: { necessary: true, analytics: true, marketing: true, functional: true }, timestamp: new Date().toISOString() });
    setBannerOpen(false);
    setModalOpen(false);
  }, [persist]);

  const rejectAll = useCallback(() => {
    persist({ accepted: true, preferences: { ...defaultPreferences }, timestamp: new Date().toISOString() });
    setBannerOpen(false);
    setModalOpen(false);
  }, [persist]);

  const savePreferences = useCallback((preferences: CookiePreferences) => {
    persist({ accepted: true, preferences, timestamp: new Date().toISOString() });
    setBannerOpen(false);
    setModalOpen(false);
  }, [persist]);

  const openModal = useCallback(() => {
    setModalOpen(true);
    setBannerOpen(false);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    if (!consent) setBannerOpen(true);
  }, [consent]);

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        bannerOpen,
        modalOpen,
        acceptAll,
        rejectAll,
        savePreferences,
        openModal,
        closeModal,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error('useCookieConsent must be used inside CookieConsentProvider');
  return ctx;
}

export { defaultPreferences };
export type { CookiePreferences, Consent, CookieCategory };
